import { ReactNode, CSSProperties, MouseEventHandler } from "react";
import { BaseComponentProps, Variant, Size } from "../types";
import "./AntdTypography.scss";

export interface BaseTypographyProps extends BaseComponentProps {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLElement>;
}

export interface AntdLinkProps extends BaseComponentProps {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  className?: string;
  style?: CSSProperties;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  underline?: boolean;
  href?: string;
}

export const AntdTypography: React.FC<BaseTypographyProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  style,
  onClick,
  testId,
}) => {
  const typographyClass = [
    "antd-typography",
    `antd-typography--${variant}`,
    `antd-typography--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={typographyClass}
      style={style}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

export const AntdTitle: React.FC<BaseTypographyProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  style,
  onClick,
  testId,
}) => {
  const titleClass = [
    "antd-title",
    `antd-title--${variant}`,
    `antd-title--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <h1
      className={titleClass}
      style={style}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </h1>
  );
};

export const AntdParagraph: React.FC<BaseTypographyProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  style,
  onClick,
  testId,
}) => {
  const paragraphClass = [
    "antd-paragraph",
    `antd-paragraph--${variant}`,
    `antd-paragraph--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <p
      className={paragraphClass}
      style={style}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </p>
  );
};

export const AntdText: React.FC<BaseTypographyProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  style,
  onClick,
  testId,
}) => {
  const textClass = [
    "antd-text",
    `antd-text--${variant}`,
    `antd-text--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={textClass}
      style={style}
      data-testid={testId}
      onClick={onClick}
    >
      {children}
    </span>
  );
};

export const AntdLink: React.FC<AntdLinkProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  style,
  testId,
  onClick,
  underline = false,
  href,
}) => {
  const linkClass = [
    "antd-link",
    `antd-link--${variant}`,
    `antd-link--${size}`,
    underline && "antd-link--underline",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick: MouseEventHandler<HTMLAnchorElement> = e => {
    if (!href) {
      e.preventDefault();
    }
    onClick?.(e);
  };

  return (
    <a
      className={linkClass}
      style={style}
      data-testid={testId}
      href={href || undefined}
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

export default AntdTypography;
